import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building } from '../../../types/protocols';
import {
    BUILDING_TABS,
    BuildingTab,
    DrawingLayer,
    parseHiddenLayers,
    parseTab,
    resolveSwitcherView,
    serializeHiddenLayers,
    switcherViews,
} from './buildingTabsView';
import { useRentableObjects } from './useRentableObjects';
import DrawingsTab from './DrawingsTab';
import ComponentCategoriesTab from './ComponentCategoriesTab';
import './BuildingTabs.css';

interface BuildingTabsProps {
    building: Building;
    buildingKey: string;
}

/**
 * The tabs below the building card (spec web/docs/specs/building-page-tabs.md):
 * Ritningar and Kategorier (lista). Every view choice lives in the URL so links and
 * the back button reproduce it.
 */
const BuildingTabs: React.FC<BuildingTabsProps> = ({ building, buildingKey }) => {
    const [params, setParams] = useSearchParams();
    const model = building.model;

    const update = useCallback((patch: Record<string, string | null>, replace = true) => {
        setParams(previous => {
            const next = new URLSearchParams(previous);
            Object.entries(patch).forEach(([key, value]) => (value === null || value === '' ? next.delete(key) : next.set(key, value)));
            return next;
        }, { replace });
    }, [setParams]);

    const tab = parseTab(params.get('tab'));
    const tabbarRef = useRef<HTMLDivElement>(null);

    // On phones the tab bar scrolls sideways; keep the active tab in view without scrolling the page.
    useEffect(() => {
        const bar = tabbarRef.current;
        const active = bar?.querySelector<HTMLElement>('.bt-tab--active');
        if (!bar || !active) return;
        const left = active.offsetLeft - bar.offsetLeft;
        if (left < bar.scrollLeft || left + active.offsetWidth > bar.scrollLeft + bar.clientWidth) {
            bar.scrollLeft = left - 16;
        }
    }, [tab]);
    // Features turned off (Inställningar → Funktioner) leave no cards, markers or layers.
    const objects = useRentableObjects(model);


    const views = useMemo(() => switcherViews(model), [model]);
    const view = resolveSwitcherView(views, params.get('drawing'), model);
    const hiddenLayers = parseHiddenLayers(params.get('hide'));

    const changeTab = (next: BuildingTab) => {
        if (next === tab) return;
        update({ tab: next === 'ritningar' ? null : next }, false);
    };

    return (
        <section className="building-tabs">
            <div className="bt-tabbar" role="tablist" ref={tabbarRef} aria-label="Visa byggnaden">
                {BUILDING_TABS.map(entry => (
                    <button
                        key={entry.key}
                        type="button"
                        role="tab"
                        aria-selected={entry.key === tab}
                        className={`bt-tab${entry.key === tab ? ' bt-tab--active' : ''}`}
                        onClick={() => changeTab(entry.key)}
                    >
                        {entry.label}
                    </button>
                ))}
            </div>

            <div className="bt-panel" role="tabpanel">
                {tab === 'ritningar' && (
                    <DrawingsTab
                        building={building}
                        buildingKey={buildingKey}
                        views={views}
                        view={view}
                        onViewChange={id => update({ drawing: id }, false)}
                        hiddenLayers={hiddenLayers}
                        onHiddenLayersChange={(hidden: Set<DrawingLayer>) => update({ hide: serializeHiddenLayers(hidden) })}
                        objects={objects}
                    />
                )}
                {tab === 'komponenter' && <ComponentCategoriesTab building={building} buildingKey={buildingKey} objects={objects} />}
            </div>
        </section>
    );
};

export default BuildingTabs;
